// src/modules/cashflow-simulator/store.js
import { defineStore } from 'pinia'
import {
  launchRun as apiLaunchRun,
  uploadFile as apiUploadFile,
  runSimulation as apiRunSimulation,
  fetchResults as apiFetchResults,
  exportResults as apiExportResults,
  getInsights as apiGetInsights, // uses axios; returns { data }
} from './api'

// ───────────────── helpers ─────────────────
function toNum(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (v == null) return null
  const s = String(v).trim().replace(/[₹,\s]/g, '').replace(/^\((.*)\)$/, '-$1')
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}
function rowsFrom(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.monthly)) return payload.monthly
  if (Array.isArray(payload?.rows)) return payload.rows
  return []
}
export function normalizeMonthly(input) {
  const arr = rowsFrom(input)
  return arr.map((r, i) => {
    const out = { ...r }
    out.label = r.label ?? r.month ?? r.Month ?? r.period ?? r.date ?? `M${i + 1}`
      ;[
        'base', 'best', 'worst',
        'closing_cash', 'closingCash',
        'ending_cash', 'endingCash',
        'cash', 'balance',
        'net_cash', 'netCash',
        'value',
      ].forEach(k => { if (k in out) out[k] = toNum(out[k]) })
    return out
  })
}

function computeSummary(rows = []) {
  if (!rows.length) return { runwayMonths: 0, minCash: 0, peakCash: 0, monthOfMinCash: '-' }
  const pick = r =>
    r.closing_cash ?? r.closingCash ??
    r.cash ?? r.balance ??
    r.net_cash ?? r.netCash ??
    r.base ?? r.value ?? null
  const vals = rows.map(pick).map(toNum)
  const labels = rows.map((r, i) => r.label ?? `M${i + 1}`)
  const numeric = vals.map((v, idx) => ({ v, idx })).filter(x => x.v != null)
  if (!numeric.length) return { runwayMonths: rows.length, minCash: 0, peakCash: 0, monthOfMinCash: labels[0] || '-' }
  const min = numeric.reduce((a, b) => (b.v < a.v ? b : a))
  const max = numeric.reduce((a, b) => (b.v > a.v ? b : a))
  return {
    runwayMonths: rows.length,
    minCash: Math.round(min.v),
    peakCash: Math.round(max.v),
    monthOfMinCash: labels[min.idx] ?? '-'
  }
}

// extract a backend error code like "CFU_400_IMMUTABLE" or return null
function pickErrorCode(e) {
  const fromApi = e?.response?.data?.error || e?.response?.data?.code || e?.response?.data?.name
  if (typeof fromApi === 'string' && /^[A-Z_0-9-]+$/.test(fromApi)) return fromApi
  return null
}

// ───────────────── store ─────────────────
export const useCashflowSimStore = defineStore('cashflow-sim', {
  state: () => ({
    sessionId: null,
    runId: null,

    // Run lifecycle
    status: null,         // 'queued' | 'running' | 'success' | 'failed' | null
    errorCode: null,      // e.g., 'CFU_400_IMMUTABLE'
    isClosed: false,      // true when run is finalized and uploads must not proceed

    // Upload detection stats
    monthsDetected: 0,         // combined mode
    salesMonthsDetected: 0,    // split mode
    expensesMonthsDetected: 0, // split mode
    uploadRows: 0,
    uploadCols: 0,

    // Back-compat aliases
    rows: 0,
    cols: 0,

    monthly: [],
    summary: null,

    loading: false,
    hasResults: false,
    error: '',
    detectedOk: false,

    // Insights
    insights: null,
    insightsLoading: false,
    insightsError: '', // keep as string for consistency
  }),

  actions: {
    resetRun() {
      this.sessionId = null
      this.runId = null

      this.status = null
      this.errorCode = null
      this.isClosed = false

      this.monthsDetected = 0
      this.salesMonthsDetected = 0
      this.expensesMonthsDetected = 0
      this.uploadRows = 0
      this.uploadCols = 0
      this.rows = 0
      this.cols = 0

      this.monthly = []
      this.summary = null
      this.hasResults = false
      this.error = ''
      this.detectedOk = false

      // reset insights
      this.insights = null
      this.insightsLoading = false
      this.insightsError = ''
    },

    async launch() {
      this.error = ''
      try {
        const data = await apiLaunchRun()
        this.sessionId = data?.sessionId ?? data?.runId ?? data?.session_id ?? null
        this.runId = data?.runId ?? data?.sessionId ?? data?.run_id ?? this.sessionId
        this.status = data?.status ?? 'queued'
        this.errorCode = null
        this.isClosed = false
        if (!this.sessionId) throw new Error('Launch failed: missing sessionId')
        return { sessionId: this.sessionId, runId: this.runId }
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Failed to launch session'
        throw e
      }
    },

    async upload(files = {}) {
      if (!this.sessionId || !this.runId) await this.launch()
      this.error = ''
      this.errorCode = null

      const fd = new FormData()
      fd.append('sessionId', this.sessionId)
      fd.append('runId', this.runId || this.sessionId)
      if (files.combined) fd.append('combined', files.combined)
      if (files.sales) fd.append('sales', files.sales)
      if (files.expenses) fd.append('expenses', files.expenses)

      try {
        const data = await apiUploadFile(fd)

        // detect which kind of file was uploaded
        const kind = data?.kind ?? (files.combined ? 'combined' : files.sales ? 'sales' : files.expenses ? 'expenses' : 'combined')
        const months = Number(toNum(data?.monthsDetected) ?? toNum(data?.months) ?? 0) || 0

        if (kind === 'combined') {
          this.monthsDetected = months
        } else if (kind === 'sales') {
          this.salesMonthsDetected = months
        } else if (kind === 'expenses') {
          this.expensesMonthsDetected = months
        }

        this.uploadRows = Number(toNum(data?.rows) ?? 0) || 0
        this.uploadCols = Number(toNum(data?.cols) ?? 0) || 0

        const enough = (v) => Number(v) >= 3
        this.detectedOk =
          enough(this.monthsDetected) ||
          (enough(this.salesMonthsDetected) && enough(this.expensesMonthsDetected))

        this.rows = this.uploadRows
        this.cols = this.uploadCols

        return {
          monthsDetected: this.monthsDetected,
          salesMonthsDetected: this.salesMonthsDetected,
          expensesMonthsDetected: this.expensesMonthsDetected,
          rows: this.uploadRows,
          cols: this.uploadCols,
        }
      } catch (e) {
        const code = pickErrorCode(e)
        this.errorCode = code
        this.error = e?.response?.data?.error || e?.message || 'Upload failed'
        // if server says the run is immutable, mark it closed so UI can pre-warn next time
        if (code === 'CFU_400_IMMUTABLE') {
          this.isClosed = true
          this.status = this.status || 'success' // conservative: treat as finished
        }
        throw e
      }
    },

    async run(scenario = {}) {
      if (!this.sessionId || !this.runId) await this.launch()
      this.loading = true
      this.error = ''
      this.errorCode = null
      try {
        const payload = { sessionId: this.sessionId, runId: this.runId, ...scenario }
        const data = await apiRunSimulation(payload)
        if (data?.monthly || data?.rows) this._applyResultsPayload(data)

        // update status if server returns it; otherwise assume success on happy path
        this.status = data?.status ?? 'success'

        // clear any previous insights (new run → new insights)
        this.insights = null
        this.insightsError = ''

        return data
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Run failed'
        this.status = this.status ?? 'failed'
        throw e
      } finally {
        this.loading = false
      }
    },

    async fetchResults({ runId, sessionId } = {}) {
      const rid = runId || this.runId
      const sid = sessionId || this.sessionId
      if (!sid && !rid) throw new Error('Missing sessionId/runId')
      this.loading = true
      this.error = ''
      try {
        const data = await apiFetchResults({ sessionId: sid, runId: rid })
        this._applyResultsPayload(data)
        this.status = data?.status ?? this.status
        this.insights = null
        this.insightsError = ''
        return { monthly: this.monthly, summary: this.summary, status: this.status }
      } catch (e) {
        // fallback: some backends accept a plain id
        try {
          const id = rid || sid
          const data2 = await apiFetchResults(id)
          this._applyResultsPayload(data2)
          this.status = data2?.status ?? this.status
          this.insights = null
          this.insightsError = ''
          return { monthly: this.monthly, summary: this.summary, status: this.status }
        } catch (err) {
          this.error = err?.response?.data?.error || err?.message || 'Failed to fetch results'
          throw err
        }
      } finally {
        this.loading = false
      }
    },

    // Hydrate status only (used when returning to Upload page)
    async fetchRunStatus(runId) {
      const rid = runId || this.runId
      if (!rid) return null
      try {
        const data = await apiFetchResults({ runId: rid, sessionId: this.sessionId })
        this.status = data?.status ?? this.status
        this.isClosed = ['success', 'failed'].includes(this.status || '')
        return this.status
      } catch {
        // ignore; UI can still rely on CFU_400_IMMUTABLE banner after an attempt
        return null
      }
    },

    async getInsights({ runId, sessionId } = {}) {
      const id = runId || this.runId || sessionId || this.sessionId;
      if (!id) { this.insightsError = 'No runId found.'; return; }
      if (this.insightsLoading) return;
      // Optional cache: don't refetch if we already have insights for this run
      if (this.insights && id === this.runId) return;

      this.insightsLoading = true;
      this.insightsError = '';
      try {
        const { data } = await apiGetInsights({ runId: id });
        const raw = data?.insights ?? data;
        this.insights = {
          narrative: String(raw?.narrative ?? ''),
          bullets: Array.isArray(raw?.bullets) ? raw.bullets : [],
          calls_to_action: Array.isArray(raw?.calls_to_action)
            ? raw.calls_to_action
            : (Array.isArray(raw?.callsToAction) ? raw.callsToAction : []),
          risk_flags: Array.isArray(raw?.risk_flags)
            ? raw.risk_flags
            : (Array.isArray(raw?.riskFlags) ? raw.riskFlags : []),
        };
      } catch (e) {
        const code = e?.response?.data?.code || e?.response?.data?.name || null;
        const msg = e?.response?.data?.error || e?.message || 'Failed to fetch insights';
        if (code === 'AI_INSIGHTS_DISABLED' || String(msg).includes('AI Insights are disabled')) {
          this.insightsError = 'Insights are disabled on the server.';
        } else if (code === 'AI_API_KEY_MISSING') {
          this.insightsError = 'Server is missing OPENAI_API_KEY.';
        } else if (code === 'AI_COMPLETION_FAILED') {
          this.insightsError = 'AI model failed to generate insights. Please try again.';
        } else {
          this.insightsError = msg;
        }
        this.insights = null;
      } finally {
        this.insightsLoading = false;
      }
    },


    async doExportXlsx() {
      if (!this.sessionId && !this.runId) throw new Error('Missing sessionId/runId')
      try {
        const data = await apiExportResults({ sessionId: this.sessionId, runId: this.runId, format: 'xlsx' })
        return { url: data?.url || data?.downloadUrl || data?.href || '' }
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Export XLSX failed'
        throw e
      }
    },
    async doExportCsv() {
      if (!this.sessionId && !this.runId) throw new Error('Missing sessionId/runId')
      try {
        const data = await apiExportResults({ sessionId: this.sessionId, runId: this.runId, format: 'csv' })
        return { url: data?.url || data?.downloadUrl || data?.href || '' }
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Export CSV failed'
        throw e
      }
    },

    // Start a brand-new run (used by "New Forecast" button)
    async newForecast() {
      this.resetRun()
      await this.launch()
    },

    _applyResultsPayload(payload) {
      const normalized = normalizeMonthly(payload?.monthly ?? payload?.rows ?? payload ?? [])
      this.monthly = normalized
      this.hasResults = this.monthly.length > 0

      const serverSummary = payload?.summary && typeof payload.summary === 'object' ? payload.summary : null
      this.summary = serverSummary || computeSummary(this.monthly)
    },

    // Back-compat aliases
    doLaunch() { return this.launch() },
    startNewSession() { return this.launch() },
    newRun() { return this.launch() },

    doUpload(files) { return this.upload(files) },
    uploadFiles(files) { return this.upload(files) },

    doRun(payload) { return this.run(payload) },
    runForecast(payload) { return this.run(payload) },

    doFetchResults() { return this.fetchResults() },
    getResults() { return this.fetchResults() },

    exportXlsx() { return this.doExportXlsx() },
    exportCsv() { return this.doExportCsv() },
  },
})
